import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { fetchMaterialIncomes, deleteMaterialIncome, createMaterialIncome, fetchColors, fetchWireTypes, type CreateMaterialIncomePayload } from '@/services/materials'
import { createMaterialExpense } from '@/services/company-expenses'
import type { MaterialIncome } from '@/types/materials'
import {
  AlertCircle,
  Package,
  Scale,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  Trash2,
  Plus,
  DollarSign,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import type { MaterialColor, MaterialType } from '@/types/materials'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Link } from 'react-router-dom'

type SortField = 'material' | 'weight' | 'price' | 'full_price' | 'left_price' | 'supplier' | 'arrived_time' | 'created_at'
type SortDirection = 'asc' | 'desc' | null

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-16 w-full' />
      ))}
    </div>
  )
}

export default function MaterialIncomes() {
  const { t, i18n } = useTranslation()
  const [incomes, setIncomes] = useState<MaterialIncome[]>([])
  const [colors, setColors] = useState<MaterialColor[]>([])
  const [wireTypes, setWireTypes] = useState<MaterialType[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingColors, setIsLoadingColors] = useState(true)
  const [isLoadingWireTypes, setIsLoadingWireTypes] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [filterColor, setFilterColor] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Delete state
  const [isDeleting, setIsDeleting] = useState<number | null>(null)

  // Create state
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [formData, setFormData] = useState({
    wire_name: '',
    color: '',
    wire_type: '',
    thickness: 0,
    weight: 0,
    price: 0,
    supplier: '',
    arrived_time: new Date().toISOString().split('T')[0],
    given_money: 0,
  })

  // Add given money dialog state
  const [isAddGivenMoneyOpen, setIsAddGivenMoneyOpen] = useState(false)
  const [selectedMaterialIncome, setSelectedMaterialIncome] = useState<MaterialIncome | null>(null)
  const [givenMoneyAmount, setGivenMoneyAmount] = useState<string>('')
  const [givenMoneyDescription, setGivenMoneyDescription] = useState<string>('')
  const [isAddingGivenMoney, setIsAddingGivenMoney] = useState(false)
  const [givenMoneyError, setGivenMoneyError] = useState<string | null>(null)

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.wire_name || !formData.color || !formData.wire_type || formData.thickness === 0 || !formData.supplier) return

    try {
      setIsCreating(true)
      const payload: CreateMaterialIncomePayload = {
        wire_name: formData.wire_name,
        color: parseInt(formData.color),
        wire_type: parseInt(formData.wire_type),
        thickness: formData.thickness,
        weight: formData.weight,
        price: formData.price,
        supplier: formData.supplier,
        arrived_time: formData.arrived_time || new Date().toISOString().split('T')[0]!,
        given_money: formData.given_money || 0,
      }
      const newIncome = await createMaterialIncome(payload)
      setIncomes((prev) => [newIncome, ...prev])
      setFormData({
        wire_name: '',
        color: '',
        wire_type: '',
        thickness: 0,
        weight: 0,
        price: 0,
        supplier: '',
        arrived_time: new Date().toISOString().split('T')[0],
        given_money: 0,
      })
      setIsCreateSheetOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create material income')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      setIsDeleting(id)
      await deleteMaterialIncome(id)
      setIncomes((prev) => prev.filter((item) => item.id !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete material income')
    } finally {
      setIsDeleting(null)
    }
  }

  const openAddGivenMoneyDialog = (income: MaterialIncome) => {
    setSelectedMaterialIncome(income)
    setGivenMoneyAmount('')
    setGivenMoneyDescription('')
    setGivenMoneyError(null)
    setIsAddGivenMoneyOpen(true)
  }

  const handleAddGivenMoney = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedMaterialIncome) return

    const amount = parseFloat(givenMoneyAmount)
    if (isNaN(amount) || amount <= 0) {
      setGivenMoneyError(t('materialIncomes.invalidGivenMoneyAmount'))
      return
    }

    try {
      setIsAddingGivenMoney(true)
      setGivenMoneyError(null)
      await createMaterialExpense(selectedMaterialIncome.id, amount, givenMoneyDescription || undefined)
      setIsAddGivenMoneyOpen(false)
      // Reload incomes to get updated given_money value
      const data = await fetchMaterialIncomes()
      setIncomes(data)
    } catch (err) {
      setGivenMoneyError(err instanceof Error ? err.message : t('errors.failedToCreate'))
    } finally {
      setIsAddingGivenMoney(false)
    }
  }

  useEffect(() => {
    const loadIncomes = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchMaterialIncomes()
        setIncomes(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load material incomes')
      } finally {
        setIsLoading(false)
      }
    }

    const loadColors = async () => {
      try {
        setIsLoadingColors(true)
        const data = await fetchColors()
        setColors(data)
      } catch (err) {
        console.error('Failed to load colors:', err)
      } finally {
        setIsLoadingColors(false)
      }
    }

    const loadWireTypes = async () => {
      try {
        setIsLoadingWireTypes(true)
        const data = await fetchWireTypes()
        setWireTypes(data)
      } catch (err) {
        console.error('Failed to load wire types:', err)
      } finally {
        setIsLoadingWireTypes(false)
      }
    }

    loadIncomes()
    loadColors()
    loadWireTypes()
  }, [])

  // Get unique colors and types for filters
  const uniqueColors = useMemo(() => {
    const colors = new Map<number, string>()
    incomes.forEach((m) => colors.set(m.material.color.id, m.material.color.name))
    return Array.from(colors, ([id, name]) => ({ id, name }))
  }, [incomes])

  const uniqueTypes = useMemo(() => {
    const types = new Map<number, string>()
    incomes.forEach((m) => types.set(m.material.type.id, m.material.type.name))
    return Array.from(types, ([id, name]) => ({ id, name }))
  }, [incomes])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortField('created_at')
        setSortDirection('desc')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className="ml-1 h-3 w-3" />
    }
    return <ArrowUpDown className="ml-1 h-3 w-3" />
  }

  const filteredIncomes = useMemo(() => {
    let result = incomes.filter((item) => {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch =
        searchQuery === '' ||
        item.material.name.toLowerCase().includes(searchLower) ||
        item.material.color.name.toLowerCase().includes(searchLower) ||
        item.material.type.name.toLowerCase().includes(searchLower) ||
        (item.supplier && item.supplier.toLowerCase().includes(searchLower))

      const matchesColor =
        filterColor === 'all' || item.material.color.id.toString() === filterColor

      const matchesType =
        filterType === 'all' || item.material.type.id.toString() === filterType

      return matchesSearch && matchesColor && matchesType
    })

    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: string | number
        let bValue: string | number

        switch (sortField) {
          case 'material':
            aValue = a.material.name
            bValue = b.material.name
            break
          case 'weight':
            aValue = a.weight
            bValue = b.weight
            break
          case 'price':
            aValue = a.price
            bValue = b.price
            break
          case 'full_price':
            aValue = a.full_price
            bValue = b.full_price
            break
          case 'left_price':
            aValue = a.left_price
            bValue = b.left_price
            break
          case 'supplier':
            aValue = a.supplier || ''
            bValue = b.supplier || ''
            break
          case 'arrived_time':
            aValue = new Date(a.arrived_time).getTime()
            bValue = new Date(b.arrived_time).getTime()
            break
          case 'created_at':
            aValue = new Date(a.created_at).getTime()
            bValue = new Date(b.created_at).getTime()
            break
          default:
            return 0
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? comparison : -comparison
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1

        return 0
      })
    }

    return result
  }, [incomes, searchQuery, filterColor, filterType, sortField, sortDirection])

  const clearFilters = () => {
    setSearchQuery('')
    setFilterColor('all')
    setFilterType('all')
  }

  const hasActiveFilters = searchQuery !== '' || filterColor !== 'all' || filterType !== 'all'

  const formatWeight = (weight: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(weight) + ' kg'
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(price)
  }

  const totalIncomes = incomes.length
  const totalWeight = incomes.reduce((sum, m) => sum + m.weight, 0)
  const totalValue = incomes.reduce((sum, m) => sum + m.full_price, 0)

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/materials'>Materials</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{t('materialIncomes.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {/* Summary Cards */}
        <div className='grid gap-4 md:grid-cols-3'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('materialIncomes.totalIncomes')}
              </CardTitle>
              <Package className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{totalIncomes}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('materialIncomes.totalWeight')}
              </CardTitle>
              <Scale className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>
                {formatWeight(totalWeight)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('materialIncomes.totalCost')}
              </CardTitle>
              <TrendingUp className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{formatPrice(totalValue)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Table Card */}
        <Card>
          <CardHeader className='flex flex-row items-center justify-between'>
            <div>
              <CardTitle>{t('materialIncomes.title')}</CardTitle>
              <CardDescription>
                {t('materialIncomes.manageIncomes')}
              </CardDescription>
            </div>
            <Sheet open={isCreateSheetOpen} onOpenChange={setIsCreateSheetOpen}>
              <SheetTrigger asChild>
                <Button>
                  <Plus className='mr-2 h-4 w-4' />
                  {t('materialIncomes.addIncome')}
                </Button>
              </SheetTrigger>
              <SheetContent className='px-4 overflow-y-auto'>
                <SheetHeader>
                  <SheetTitle>{t('materialIncomes.createIncome')}</SheetTitle>
                  <SheetDescription>
                    {t('materialIncomes.createIncomeDesc')}
                  </SheetDescription>
                </SheetHeader>
                <form onSubmit={handleCreate} className='flex flex-col gap-4 py-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='wire_name'>{t('materialIncomes.wireName')}</Label>
                    <Input
                      id='wire_name'
                      placeholder={t('materialIncomes.enterWireName')}
                      value={formData.wire_name}
                      onChange={(e) => setFormData({ ...formData, wire_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='color'>{t('materialIncomes.color')}</Label>
                    <Select
                      value={formData.color}
                      onValueChange={(value) =>
                        setFormData({ ...formData, color: value })
                      }
                    >
                      <SelectTrigger id='color' className='w-full'>
                        <SelectValue placeholder={t('materialIncomes.selectColor')} />
                      </SelectTrigger>
                      <SelectContent>
                        {colors.map((color) => (
                          <SelectItem key={color.id} value={color.id.toString()}>
                            {color.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='wire_type'>{t('materialIncomes.wireType')}</Label>
                    <Select
                      value={formData.wire_type}
                      onValueChange={(value) =>
                        setFormData({ ...formData, wire_type: value })
                      }
                    >
                      <SelectTrigger id='wire_type' className='w-full'>
                        <SelectValue placeholder={t('materialIncomes.selectWireType')} />
                      </SelectTrigger>
                      <SelectContent>
                        {wireTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id.toString()}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='thickness'>{t('materialIncomes.thickness')}</Label>
                    <Input
                      id='thickness'
                      type='number'
                      step='0.01'
                      placeholder='0.00'
                      value={formData.thickness || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, thickness: parseFloat(e.target.value) || 0 })
                      }
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='weight'>{t('materialIncomes.quantity')}</Label>
                    <Input
                      id='weight'
                      type='number'
                      step='0.01'
                      placeholder={t('materialIncomes.enterQuantity')}
                      value={formData.weight || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, weight: parseFloat(e.target.value) || 0 })
                      }
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='price'>{t('materialIncomes.pricePerKg')}</Label>
                    <Input
                      id='price'
                      type='number'
                      step='0.01'
                      placeholder={t('materialIncomes.enterPrice')}
                      value={formData.price || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })
                      }
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='given_money'>{t('materialIncomes.givenMoney')}</Label>
                    <Input
                      id='given_money'
                      type='number'
                      step='0.01'
                      placeholder={t('materialIncomes.enterGivenMoney')}
                      value={formData.given_money || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, given_money: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='supplier'>{t('materialIncomes.supplier')}</Label>
                    <Input
                      id='supplier'
                      placeholder={t('materialIncomes.enterSupplier')}
                      value={formData.supplier}
                      onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='arrived_time'>{t('materialIncomes.arrivalDate')}</Label>
                    <Input
                      id='arrived_time'
                      type='date'
                      value={formData.arrived_time}
                      onChange={(e) =>
                        setFormData({ ...formData, arrived_time: e.target.value })
                      }
                      required
                    />
                  </div>
                  <SheetFooter className='mt-4'>
                    <Button type='submit' disabled={isCreating || !formData.wire_name || !formData.color || !formData.wire_type || formData.thickness === 0 || !formData.supplier}>
                      {isCreating ? t('materialIncomes.creating') : t('common.create')}
                    </Button>
                  </SheetFooter>
                </form>
              </SheetContent>
            </Sheet>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className='mb-4 flex flex-wrap items-center gap-3'>
              <div className='relative flex-1 min-w-50'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder={t('materialIncomes.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
              <Select value={filterColor} onValueChange={setFilterColor}>
                <SelectTrigger className='w-35'>
                  <SelectValue placeholder={t('materialIncomes.color')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('materialIncomes.allColors')}</SelectItem>
                  {uniqueColors.map((color) => (
                    <SelectItem key={color.id} value={color.id.toString()}>
                      {color.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className='w-40'>
                  <SelectValue placeholder={t('materialIncomes.type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('materialIncomes.allTypes')}</SelectItem>
                  {uniqueTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant='ghost' size='sm' onClick={clearFilters}>
                  <X className='mr-1 h-4 w-4' />
                  {t('common.clear')}
                </Button>
              )}
            </div>

            {isLoading ? (
              <LoadingSkeleton />
            ) : filteredIncomes.length === 0 ? (
              <div className='py-8 text-center text-muted-foreground'>
                {incomes.length === 0
                  ? t('materialIncomes.noIncomesFound')
                  : t('materialIncomes.noIncomesMatch')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('material')}
                    >
                      <div className='flex items-center'>
                        {t('materialIncomes.material')}
                        {getSortIcon('material')}
                      </div>
                    </TableHead>
                    <TableHead>{t('materialIncomes.type')}</TableHead>
                    <TableHead>{t('materialIncomes.color')}</TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('weight')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('materialIncomes.weight')}
                        {getSortIcon('weight')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('price')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('materialIncomes.priceKg')}
                        {getSortIcon('price')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('full_price')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('materialIncomes.fullPrice')}
                        {getSortIcon('full_price')}
                      </div>
                    </TableHead>
                    <TableHead className='text-right'>
                      {t('materialIncomes.givenMoney')}
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('left_price')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('materialIncomes.leftPrice')}
                        {getSortIcon('left_price')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('supplier')}
                    >
                      <div className='flex items-center'>
                        {t('materialIncomes.supplier')}
                        {getSortIcon('supplier')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('arrived_time')}
                    >
                      <div className='flex items-center'>
                        {t('materialIncomes.arrived')}
                        {getSortIcon('arrived_time')}
                      </div>
                    </TableHead>
                    <TableHead className='w-20'>{t('materialIncomes.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredIncomes.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className='font-medium'>{item.material.name}</div>
                        <div className='text-xs text-muted-foreground'>
                          {item.material.thickness} mm
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className='rounded-full bg-muted px-2 py-1 text-xs font-medium'>
                          {item.material.type.name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className='rounded-full bg-primary/10 text-primary px-2 py-1 text-xs font-medium'>
                          {item.material.color.name}
                        </span>
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        {formatWeight(item.weight)}
                      </TableCell>
                      <TableCell className='text-right'>
                        {formatPrice(item.price)}
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        {formatPrice(item.full_price)}
                      </TableCell>
                      <TableCell className='text-right'>
                        {formatPrice(item.given_money ?? 0)}
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        {formatPrice(item.left_price)}
                      </TableCell>
                      <TableCell className='text-center'>
                        {item.supplier || (
                          <span className='text-muted-foreground'>-</span>
                        )}
                      </TableCell>
                      <TableCell className='text-muted-foreground'>
                        {formatDate(item.arrived_time)}
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-1'>
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-8 w-8 text-muted-foreground hover:text-green-600'
                            onClick={() => openAddGivenMoneyDialog(item)}
                            title={t('materialIncomes.addGivenMoney')}
                          >
                            <DollarSign className='h-4 w-4' />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant='ghost'
                                size='icon'
                                className='h-8 w-8 text-muted-foreground hover:text-destructive'
                                disabled={isDeleting === item.id}
                              >
                                <Trash2 className='h-4 w-4' />
                              </Button>
                            </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t('materialIncomes.deleteTitle')}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('materialIncomes.deleteDescription')}{' '}
                                <span className='font-medium'>{item.material.name}</span>? {t('materialIncomes.deleteWarning')}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(item.id)}
                                className='bg-destructive cursor-pointer hover:bg-destructive/90'
                              >
                                {t('common.delete')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Results count and total left price */}
            {!isLoading && incomes.length > 0 && (
              <div className='mt-4 flex items-center justify-between text-sm text-muted-foreground'>
                <span>
                  {t('common.showing')} {filteredIncomes.length} {t('common.of')} {incomes.length}
                </span>
                {(() => {
                  const totalLeftPrice = filteredIncomes.reduce(
                    (sum, item) => sum + item.left_price,
                    0
                  )
                  return (
                    <span className='font-medium'>
                      {t('materialIncomes.totalLeftPrice')}:{' '}
                      <span className='text-red-600'>
                        {formatPrice(totalLeftPrice)}
                      </span>
                    </span>
                  )
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Given Money Sheet */}
      <Sheet open={isAddGivenMoneyOpen} onOpenChange={setIsAddGivenMoneyOpen}>
        <SheetContent className='px-4'>
          <SheetHeader>
            <SheetTitle>{t('materialIncomes.addGivenMoney')}</SheetTitle>
            <SheetDescription>
              {selectedMaterialIncome && t('materialIncomes.addGivenMoneyDesc', {
                material: selectedMaterialIncome.material.name,
              })}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAddGivenMoney}>
            <div className='grid gap-4 py-4'>
              <div className='space-y-2'>
                <Label htmlFor='given_money_amount'>{t('materialIncomes.amount')}</Label>
                <Input
                  id='given_money_amount'
                  type='number'
                  step='0.01'
                  placeholder={t('materialIncomes.enterGivenMoney')}
                  value={givenMoneyAmount}
                  onChange={(e) => setGivenMoneyAmount(e.target.value)}
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='given_money_description'>{t('expenses.descriptionOptional')}</Label>
                <Input
                  id='given_money_description'
                  placeholder={t('expenses.enterDescription')}
                  value={givenMoneyDescription}
                  onChange={(e) => setGivenMoneyDescription(e.target.value)}
                />
              </div>
              {givenMoneyError && (
                <div className='flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
                  <AlertCircle className='h-4 w-4' />
                  <span>{givenMoneyError}</span>
                </div>
              )}
            </div>
            <SheetFooter className='mt-4'>
              <Button type='submit' disabled={isAddingGivenMoney}>
                {isAddingGivenMoney ? t('common.saving') : t('common.save')}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </>
  )
}
